/**
 * Keyboard Game - Daily Typing Challenge
 * Copyright (c) 2025 All Rights Reserved
 * 
 * This software and its source code are protected by copyright law.
 * Unauthorized copying, modification, or distribution is prohibited.
 */

import { useState, useEffect, useCallback } from 'react';
import GameHeader from '@/components/game/GameHeader';
import GameStart from '@/components/game/GameStart';
import GamePlay from '@/components/game/GamePlay';
import GameResults from '@/components/game/GameResults';
import Leaderboard from '@/components/game/Leaderboard';
import UsernameModal from '@/components/ui/UsernameModal';
import ChallengeModal from '@/components/game/ChallengeModal';
import Footer from '@/components/Footer';
import { getDailyChallenge } from '@/utils/wordGenerator';
import { getStreakData, updateStreak } from '@/utils/streakManager';
import { calculateWPM, calculateAccuracy } from '@/utils/statsCalculator';
import { submitScore, getChallenge } from '@/utils/api';
import { useUser } from '@/contexts/UserContext';
import { toast } from 'sonner';

export default function GamePage() {
  const [gameState, setGameState] = useState('start'); // start, playing, complete
  const [dailyWords, setDailyWords] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [difficulty, setDifficulty] = useState('hard'); // Always use hard mode
  const [streakData, setStreakData] = useState(getStreakData());
  const [mistakes, setMistakes] = useState(0);
  const [keystrokeData, setKeystrokeData] = useState([]);
  const [lastKeystrokeTime, setLastKeystrokeTime] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [challengeData, setChallengeData] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const { user, loading: userLoading } = useUser();
  const [stats, setStats] = useState({
    todayTime: null,
    bestTime: null,
    gamesPlayed: 0,
    streak: 0,
    wpm: 0,
    accuracy: 100
  });

  // Check if user needs to register (only once on mount)
  useEffect(() => {
    if (!userLoading && !user) {
      // Double check localStorage before showing modal
      const storedUserId = localStorage.getItem('keyboard_user_id');
      const storedUsername = localStorage.getItem('keyboard_username');
      
      if (!storedUserId || !storedUsername) {
        // Show username modal after a short delay
        setTimeout(() => setShowUsernameModal(true), 500);
      }
    }
  }, [user, userLoading]);
  
  // Check for challenge URL on mount
  useEffect(() => {
    const checkForChallenge = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const challengeId = urlParams.get('challenge');
        
        if (challengeId) {
          const result = await getChallenge(challengeId);
          if (result.success) {
            setChallengeData(result.data);
            setShowChallengeModal(true);
          } else {
            console.error('Challenge fetch error:', result.error);
            toast.error('Challenge not found or expired');
          }
        }
      } catch (error) {
        console.error('Error checking for challenge:', error);
        // Don't show error toast for network errors
      }
    };
    
    checkForChallenge();
  }, []);

  // Load daily content and stats on mount
  useEffect(() => {
    loadDailyContent();
    loadStats();
    setStreakData(getStreakData());
  }, [difficulty]);
  
  const loadDailyContent = () => {
    const challenge = getDailyChallenge(difficulty);
    setDailyWords(challenge.words);
  };

  const loadStats = () => {
    const savedStats = localStorage.getItem('keyboard_stats');
    if (savedStats) {
      setStats(JSON.parse(savedStats));
    }
  };

  const saveStats = (completionTime, wpm, accuracy) => {
    const newStats = {
      todayTime: completionTime,
      bestTime: stats.bestTime ? Math.min(stats.bestTime, completionTime) : completionTime,
      gamesPlayed: stats.gamesPlayed + 1,
      streak: stats.streak + 1,
      wpm,
      accuracy
    };
    localStorage.setItem('keyboard_stats', JSON.stringify(newStats));
    setStats(newStats);
  };

  // Track keystroke timing for anti-cheat
  const trackKeystroke = () => {
    const now = Date.now();
    if (lastKeystrokeTime) {
      const timeDiff = now - lastKeystrokeTime;
      setKeystrokeData(prev => [...prev, timeDiff]);
    }
    setLastKeystrokeTime(now);
  };

  const handleStartGame = () => {
    setGameState('playing');
    setStartTime(Date.now());
    setCurrentWordIndex(0);
    setTypedText('');
    setMistakes(0);
    setKeystrokeData([]);
    setLastKeystrokeTime(null);
    
    toast.success(`Challenge started! Good luck!`);
  };

  const handleGameComplete = async () => {
    const completionTime = Date.now() - startTime;
    setEndTime(completionTime);
    setGameState('complete');
    
    // Calculate WPM and accuracy
    const totalText = dailyWords.join('');
    const wpm = calculateWPM(totalText, completionTime);
    const accuracy = calculateAccuracy(totalText.length, totalText.length, mistakes);
    
    // Update streak
    const newStreakData = updateStreak();
    setStreakData(newStreakData);
    
    // Save stats with WPM and accuracy
    const newStats = {
      todayTime: completionTime,
      bestTime: stats.bestTime ? Math.min(stats.bestTime, completionTime) : completionTime,
      gamesPlayed: stats.gamesPlayed + 1,
      streak: newStreakData.currentStreak,
      wpm,
      accuracy
    };
    localStorage.setItem('keyboard_stats', JSON.stringify(newStats));
    setStats(newStats);
    
    // Submit to leaderboard if user is logged in
    if (user) {
      await submitScoreToLeaderboard({
        wpm,
        accuracy,
        time_seconds: completionTime / 1000,
        score: 0,
        mistakes,
        streak: newStreakData.currentStreak
      });
    }
    
    toast.success('Congratulations! Challenge completed!');
  };

  const handleTriviaComplete = async (results) => {
    const completionTime = Date.now() - startTime;
    setEndTime(completionTime);
    setGameState('complete');
    setTriviaResults(results);
    
    // Calculate trivia score
    const score = calculateTriviaScore(
      results.timeSeconds,
      results.correctCount,
      results.totalQuestions,
      results.maxStreak
    );
    
    // Calculate WPM based on total characters typed
    const totalChars = results.answers.reduce((sum, a) => sum + a.userAnswer.length, 0);
    const wpm = calculateWPM({ length: totalChars }, completionTime);
    const accuracy = (results.correctCount / results.totalQuestions) * 100;
    
    // Update streak
    const newStreakData = updateStreak();
    setStreakData(newStreakData);
    
    // Save stats
    const newStats = {
      todayTime: completionTime,
      bestTime: stats.bestTime ? Math.min(stats.bestTime, completionTime) : completionTime,
      gamesPlayed: stats.gamesPlayed + 1,
      streak: newStreakData.currentStreak,
      wpm,
      accuracy
    };
    localStorage.setItem('keyboard_stats', JSON.stringify(newStats));
    setStats(newStats);
    
    // Submit to leaderboard if user is logged in
    if (user) {
      await submitScoreToLeaderboard({
        wpm,
        accuracy,
        time_seconds: results.timeSeconds,
        score,
        mistakes: results.totalQuestions - results.correctCount,
        streak: newStreakData.currentStreak
      });
    }
    
    toast.success(`Trivia Complete! Score: ${score}`);
  };

  const submitScoreToLeaderboard = async (scoreData) => {
    try {
      const result = await submitScore({
        user_id: user.id,
        username: user.username,
        mode: 'words',
        difficulty: difficulty,
        ...scoreData,
        keystroke_data: keystrokeData.slice(0, 50), // Send first 50 keystrokes
        total_keystrokes: keystrokeData.length
      });
      
      if (result.success) {
        toast.success('Score submitted to leaderboard!');
      } else {
        if (result.error.includes('rejected')) {
          toast.error('Score not submitted: Suspicious activity detected');
        } else {
          toast.error('Failed to submit score');
        }
      }
    } catch (error) {
      console.error('Error submitting score:', error);
    }
  };

  const handlePlayAgain = () => {
    loadDailyContent();
    setGameState('start');
    setStartTime(null);
    setEndTime(null);
    setCurrentWordIndex(0);
    setTypedText('');
    setMistakes(0);
    setKeystrokeData([]);
    setLastKeystrokeTime(null);
    setTriviaResults(null);
  };
  
  const handleAcceptChallenge = () => {
    // Set game mode and difficulty from challenge
    // Accept challenge - only use difficulty since we only have words mode
    setDifficulty(challengeData.difficulty);
    setShowChallengeModal(false);
    
    toast.success(`Challenge accepted! Try to beat ${challengeData.challenger_username}'s score!`);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <GameHeader 
        stats={stats} 
        onShowLeaderboard={() => setShowLeaderboard(true)}
      />
      
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        {gameState === 'start' && (
          <GameStart 
            onStart={handleStartGame} 
            dailyWords={dailyWords}
            countdown={countdown}
            setCountdown={setCountdown}
          />
        )}
        
        {gameState === 'playing' && (
          <GamePlay
            dailyWords={dailyWords}
            startTime={startTime}
            currentWordIndex={currentWordIndex}
            setCurrentWordIndex={setCurrentWordIndex}
            typedText={typedText}
            setTypedText={setTypedText}
            onComplete={handleGameComplete}
            difficulty={difficulty}
            mistakes={mistakes}
            setMistakes={setMistakes}
            onKeystroke={trackKeystroke}
          />
        )}
        
        
        {gameState === 'complete' && (
          <GameResults
            completionTime={endTime}
            stats={stats}
            streakData={streakData}
            difficulty={difficulty}
            gameMode="words"
            onPlayAgain={handlePlayAgain}
          />
        )}
      </main>
      
      {/* Modals */}
      {showLeaderboard && (
        <Leaderboard onClose={() => setShowLeaderboard(false)} />
      )}
      
      {showUsernameModal && (
        <UsernameModal 
          open={showUsernameModal} 
          onClose={() => setShowUsernameModal(false)} 
        />
      )}
      
      {showChallengeModal && challengeData && (
        <ChallengeModal
          open={showChallengeModal}
          challenge={challengeData}
          onAccept={handleAcceptChallenge}
          onDecline={() => setShowChallengeModal(false)}
        />
      )}
      
      <Footer />
    </div>
  );
}